export type PillarLine = {code:string;unit:string;quantity:number};
export type PillarPlan = {
 enabled:boolean; quantities:Record<string,number|null>; area:number|null;
 notes:string[]; blocked:string[]; key:string;
};
const number = (v:string|undefined, optional=false):number|null => {
 if (!v?.trim()) return optional ? 0 : null;
 const n=Number(v.replace(",","."));
 return Number.isFinite(n) && n>=0 && n<=1000000 ? n : null;
};
export function reinforcedConcreteConsumptionPlan(workCode:string,sourceCode:string|undefined,lines:PillarLine[],
 origin:Record<string,string>,values:Record<string,string>):PillarPlan {
 const mappings:Record<string,{source:string;steel:string}>={
  "BA.VERT.PILAR":{source:"EHS010",steel:"mt07aco040b"},
  "BA.HOR.VIGA":{source:"EHV010",steel:"mt07aco040b"},
  "BA.VERT.MURO":{source:"EHM010",steel:"mt07aco040e"},
 };
 const mapping=mappings[workCode];
 const normalize=(unit:string)=>unit.trim().toLowerCase().replace("³","3");
 const steelLines=lines.filter(l=>l.code===mapping?.steel && normalize(l.unit)==="kg");
 const concreteLines=lines.filter(l=>l.code==="mt10haf020bgngc" && normalize(l.unit)==="m3");
 const originalSteel=number(origin.reinforcement_kg_m3),originalWaste=number(origin.steel_waste_pct,true);
 const originalExcess=number(origin.concrete_excess_pct,true);
 const enabled=!!mapping && sourceCode===mapping.source && steelLines.length===1 && concreteLines.length===1
   && originalSteel!==null && originalWaste!==null && originalExcess!==null
   && Math.abs(steelLines[0].quantity-originalSteel*(1+originalWaste/100))<0.000001
   && Math.abs(concreteLines[0].quantity-(1+originalExcess/100))<0.000001;
 if(!enabled) return {enabled:false,quantities:{},area:null,notes:[],blocked:[],key:""};
 const steel=number(values.reinforcement_kg_m3),steelWaste=number(values.steel_waste_pct,true);
 const excess=number(values.concrete_excess_pct,true);
 let area:number|null=null;
 if(workCode==="BA.VERT.PILAR") {
  const a=number(values.section_a),b=number(values.section_b);
  if(a!==null && b!==null && a>0 && b>0) area=2*(a+b)/(a*b);
 } else if(workCode==="BA.HOR.VIGA") {
  const w=number(values.width),h=number(values.height);
  if(w!==null && h!==null && w>0 && h>0) area=(w+2*h)/(w*h);
 } else {
  const t=number(values.thickness),faces=values.formwork_faces==="one" ? 1 : values.formwork_faces==="two" ? 2 : null;
  if(t!==null && t>0 && faces!==null) area=faces/t;
 }
 const quantities:Record<string,number|null>={
  [mapping.steel]:steel!==null && steelWaste!==null ? Number((steel*(1+steelWaste/100)).toFixed(6)) : null,
  mt10haf020bgngc:excess!==null ? Number((1+excess/100).toFixed(6)) : null,
 };
 const blocked:string[]=[];
 for(const key of ["steel_grade","concrete_strength","exposure_class","placement_method","formwork_system"]) {
  if(values[key]!==origin[key]) blocked.push("A opção «"+({
   steel_grade:"Aço",concrete_strength:"Classe do betão",exposure_class:"Exposição",
   placement_method:"Betonagem",formwork_system:"Sistema de cofragem",
  } as Record<string,string>)[key]+"» exige recursos e preços compatíveis. Selecione uma ficha correspondente ou reponha esta opção.");
 }
 const notes=[
  "Betão: 1 m³ × (1 + excesso / 100). Aço: kg/m³ × (1 + desperdício / 100).",
  workCode==="BA.VERT.PILAR" ? "Pilar: área de cofragem = 2 × (A + B) / (A × B), por m³."
   : workCode==="BA.HOR.VIGA" ? "Viga retangular: fundo e duas faces laterais, sem topo nem topos. Área = (largura + 2 × altura) / (largura × altura), por m³."
   : "Muro plano: área de cofragem = número de faces / espessura, por m³, sem topos.",
  "Os recursos amortizados de cofragem exigem revisão própria; a área geométrica não altera automaticamente esses consumos.",
  "Mão de obra, equipamentos e restantes recursos mantêm os consumos da origem até serem revistos.",
 ];
 return {enabled,quantities,area,notes,blocked,key:JSON.stringify(quantities)};
}

/** Compatibility entry point for existing pillar integrations. */
export function pillarConsumptionPlan(workCode:string,sourceCode:string|undefined,lines:PillarLine[],
 origin:Record<string,string>,values:Record<string,string>):PillarPlan {
 if(workCode!=="BA.VERT.PILAR") return {enabled:false,quantities:{},area:null,notes:[],blocked:[],key:""};
 return reinforcedConcreteConsumptionPlan(workCode,sourceCode,lines,origin,values);
}
